import {
  browser, Layout, log, util
} from '@glu/core';
import AdoptRegion from './adoptRegion';
import EmptyView from './body/emptyView';

import template from './tableLayout.hbs';

export default Layout.extend({
  template,
  className: 'table-layout',

  regions: {
    caption: 'caption',
    header: 'thead',
    body: {
      selector: 'tbody',
      regionType: AdoptRegion
    },
    footer: 'tfoot',
    info: '[data-hook="infoContainer"]'
  },

  events: {
    'mouseover td[data-title]': 'addTitle',
    'mouseout td[data-generated-title]': 'removeTitle'
  },

  appEvents: {
    resize: 'setWidth'
  },

  addTitle(e) {
    const $el = this.$(e.currentTarget);
    // if title is empty or undefined
    if (!$el.attr('title')) {
      $el.attr({
        title: util.unescape($el.data('title')), // 'title' is a safe attribute for untrusted data
        'data-generated-title': 'true'
      });
    }
  },

  removeTitle(e) {
    const $el = this.$(e.currentTarget);

    $el.removeAttr('title');
    $el.removeAttr('data-generated-title');
  },

  initialize(...args) {
    Layout.prototype.initialize.apply(this, args);

    this.defaultWidth = this.options.defaultWidth || 100;
    this.grid = this.options.grid;
    this.masterCollection = this.options.masterCollection;
    this.loading = false;

    // Determine whether to load with an immediate loading state based on
    // collection's `requestingAttributes` flag
    if (this.options.serverSidePagination) {
      this.loading = this.masterCollection.requestingAttributes;
    }

    // TODO `desync` event is deprecated, all logic now hangs off of the Backbone event `request`
    this.listenTo(this.masterCollection, {
      'desync request': this.showLoadingView,
      'sync remove': this.hideInfoView
    });

    this.listenTo(this.grid, {
      'grid:contentRendered': this.setWidth
    });

    this.listenTo(this.grid.columns, {
      'change:width': this.setWidth,
      'change:condition reorder': () => {
        if (browser.msie) {
          this.render();
        }
      }
    });
  },

  setCaption(caption) {
    this.captionView = caption;
  },

  setHeader(header) {
    this.headerView = header;
  },

  setBody(body) {
    this.bodyView = body;
  },

  onRender() {
    if (this.captionView) {
      this.caption.show(this.captionView);
    } else {
      this.removeRegion('caption');
    }
    if (this.headerView) {
      this.header.show(this.headerView);
      this.listenTo(this.headerView.collection, 'sync', this.setWidth);
    }
    if (this.bodyView) {
      this.body.show(this.bodyView);
      this.listenTo(this.bodyView.collection, 'add reset remove', this.toggleEmptyView);
    }

    if (this.loading) {
      this.showLoadingView();
    }

    this.toggleEmptyView();
  },

  setWidth() {
    const $table = this.$('table');
    const $canvas = this.$('.js-canvas');
    const $footer = this.grid.$('.footer');
    const viewportWidth = this.$el.width();
    let tableWidth = $table.outerWidth();
    let calculatedWidth = 0;

    $canvas.width(viewportWidth);
    $table.width(viewportWidth);
    $footer.width(viewportWidth);

    // Grids with disableDynamicColumnWidths enabled do not use this function for table column widths, they use CSS only to manage widths
    // But sticky footers need to have their width set regardless of this setting
    if (this.options.disableDynamicColumnWidths || this.isClosed) {
      // trigger event to update footer position
      this.grid.trigger('grid:columnWidthUpdated');
      return false;
    }

    $table.find('thead th').each((i, el) => {
      calculatedWidth += this.getColumnWidth(this.$(el));
    });

    if (tableWidth > calculatedWidth) {
      tableWidth = calculatedWidth;
    }

    $table.width(tableWidth < viewportWidth ? viewportWidth : tableWidth);
    $table.find('thead th').each((i, el) => {
      this.setColumnWidth(this.$(el));
    });

    // trigger event to update footer position
    this.grid.trigger('grid:columnWidthUpdated');

    // if grid is inside a dashboard region
    if (this.$el.closest('.dashboard-region').length) {
      // reset the inline width (GLU-1096)
      $footer.css('width', 'auto');
    }
  },

  /**
   * @method getInitialWidth
   * @param {string} initialWidth - column header init width data attr
   * @param {number} outerWidth - actual computed width from jquery
   * @return {number}
   * - This gives us the column header width
   */
  getInitialWidth(initialWidth, outerWidth) {
    if (util.isUndefined(initialWidth)) {
      return outerWidth;
    }

    if (util.isNumber(initialWidth)) {
      return initialWidth;
    }

    // After we handle type cases deal with CSS units and return a number
    const match = initialWidth.match(/^(-?\d+(?:\.\d+)?)([a-z%]+)?$/i);

    if (util.isNull(match)) {
      return outerWidth;
    }

    if (util.isUndefined(match[2]) || (/px/i).test(match[2])) {
      // undefined unit or pixels (regardless of case), just use the number
      return parseFloat(match[1]);
    }
    // Unit is not pixels, log a warning
    log.info('tableLayout.js > getColumnWidth $th.data(initialWidth) incorrect unit');
    return outerWidth;
  },

  /**
   * @method getInitialWidthFromDOM
   * @param {jQuery} $th - column header
   * @return {number}
   * - This extracts DOM information for initial width calculation
   */
  getInitialWidthFromDOM($th) {
    return this.getInitialWidth($th.data('initialWidth'), $th.outerWidth());
  },

  /**
   * @method getColumnWidth
   * @param {jQuery} $th - column header
   * @return {number}
   * - Returns column width (actual or default)
   */
  getColumnWidth($th) {
    let initialWidth = this.getInitialWidthFromDOM($th);

    initialWidth = initialWidth === 'auto' ? $th.outerWidth() : initialWidth;

    // FIXME: changing values in a 'get' method is bad practice
    // re-set 'auto' width for columns in case if scroll appears
    if ($th.data('gridColumnWidth') === 'default') {
      $th.css({
        width: 'auto',
        minWidth: 'auto'
      });

      initialWidth = $th.outerWidth() > 0 ? $th.outerWidth() : this.defaultWidth;
    }

    return parseInt(initialWidth, 10);
  },

  setColumnWidth($th) {
    const cellData = $th.data();
    let initialWidth = this.getColumnWidth($th);
    const headerModel = this.grid.columns.get(cellData.cid);

    initialWidth = initialWidth < this.defaultWidth && cellData.gridColumnWidth === 'default' ? this.defaultWidth : initialWidth;

    $th.css({
      width: initialWidth,
      minWidth: initialWidth,
      maxWidth: initialWidth
    });

    if (!headerModel) {
      return; // some th elements do not have an associated model
    }

    headerModel.set('setWidth', initialWidth, {
      silent: true
    });
  },

  toggleEmptyView() {
    if (this.loading) {
      return;
    }
    if (this.bodyView && !this.bodyView.collection.isEmpty()) {
      this.info.reset();
    } else {
      this.info.show(new EmptyView({
        resultsName: this.grid.options.resultsName,
        text: this.options.emptyViewText
      }));

      this.grid.trigger('grid:content:empty');
    }
  },

  showLoadingView(model, xhr, options) {
    options = options || {};

    if (options.skipLoading) {
      return;
    }

    this.loading = true;

    const LoadingView = this.options.loadingView;
    this.info.show(new LoadingView({
      text: this.options.loadingViewText
    }));
  },

  hideInfoView() {
    this.loading = false;

    // this needs for case when master collection is empty in all other cases `this.toggleEmptyView` method
    // will be triggered after setting models to masterCollection
    if (this.masterCollection.isEmpty()) {
      this.toggleEmptyView();
    }
  }
});
